import { useCallback, useEffect, useState } from "react";
import { errMsg } from "@/lib/errors";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { UserCog, Trash2, CalendarClock, X } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_SHORT = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };
const DEFAULT_HOURS = { monday: { open: true, start: "09:00", end: "17:00" }, tuesday: { open: true, start: "09:00", end: "17:00" }, wednesday: { open: true, start: "09:00", end: "17:00" }, thursday: { open: true, start: "09:00", end: "17:00" }, friday: { open: true, start: "09:00", end: "17:00" }, saturday: { open: false, start: "09:00", end: "17:00" }, sunday: { open: false, start: "09:00", end: "17:00" } };

export default function Team() {
  const [staff, setStaff] = useState([]);
  const [bizHours, setBizHours] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [drafts, setDrafts] = useState({});   // staffId -> hours draft
  const [expanded, setExpanded] = useState({});
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    api.get("/staff").then(r => setStaff(r.data)).catch((e) => { console.warn("staff load failed:", e); setLoadError(true); });
    api.get("/business").then(r => setBizHours(r.data.hours || {})).catch((e) => console.warn("business load failed:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addStaff = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/staff", { name: name.trim(), role: role.trim() || "Staff" });
      setName(""); setRole("");
      toast.success("Team member added");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not add team member")); }
  };

  const removeStaff = async (id) => {
    if (!window.confirm("Remove this team member and their schedule? Their bookings stay in history.")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  const startSchedule = (s) => {
    const src = (s.hours && Object.keys(s.hours).length) ? s.hours
      : (bizHours && Object.keys(bizHours).length) ? bizHours : DEFAULT_HOURS;
    setDrafts({ ...drafts, [s.id]: JSON.parse(JSON.stringify(src)) });
    setExpanded({ ...expanded, [s.id]: true });
  };

  const setDay = (sid, day, patch) => {
    const cur = drafts[sid] || {};
    const d = cur[day] || { open: false, start: "09:00", end: "17:00" };
    setDrafts({ ...drafts, [sid]: { ...cur, [day]: { ...d, ...patch } } });
  };

  const saveSchedule = async (sid) => {
    try {
      await api.patch(`/staff/${sid}`, { hours: drafts[sid] });
      toast.success("Schedule saved — the AI will only offer this person during these hours");
      load();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
  };

  const clearSchedule = async (sid) => {
    try {
      await api.patch(`/staff/${sid}`, { hours: {} });
      setDrafts({ ...drafts, [sid]: undefined });
      setExpanded({ ...expanded, [sid]: false });
      toast.success("Schedule cleared — works all business hours");
      load();
    } catch (e) { toast.error("Could not clear"); }
  };

  return (
    <div className="p-6 max-w-3xl" data-testid="team-page">
      <h1 className="font-heading text-2xl font-bold mb-1">Team</h1>
      <p className="text-sm text-slate-500 mb-6">Who callers can book with. Set each person's working days/hours — the AI only books them when they're in, and offers someone free when they're not.</p>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <div className="font-semibold text-sm mb-3">Add team member</div>
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="add-staff-name" placeholder="Name (e.g. John)" value={name} onChange={(e) => setName(e.target.value)} className="w-48" />
          <Input data-testid="add-staff-role" placeholder="Role (e.g. Nail tech)" value={role} onChange={(e) => setRole(e.target.value)} className="w-48" />
          <Button data-testid="add-staff-btn" onClick={addStaff} className="rounded-xl bg-blue-600 hover:bg-blue-700">Add</Button>
        </div>
      </div>

      <div className="space-y-3">
        {loadError && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="team-load-error">Couldn't load your team — refresh to retry.</p>}
        {staff.length === 0 && !loadError && <p className="text-sm text-slate-500" data-testid="team-empty">No team members yet — add your first above.</p>}
        {staff.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`staff-row-${s.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center"><UserCog className="w-4 h-4 text-blue-600" /></div>
                <div>
                  <div className="font-semibold text-sm" data-testid={`staff-name-${s.id}`}>{s.name}</div>
                  <div className="text-xs text-slate-500">{s.role || "Staff"} · {!s.hours || !Object.keys(s.hours).length ? "works all business hours" : "custom schedule"}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-xl" data-testid={`schedule-btn-${s.id}`}
                  onClick={() => drafts[s.id] ? setExpanded({ ...expanded, [s.id]: !expanded[s.id] }) : startSchedule(s)}>
                  <CalendarClock className="w-4 h-4 mr-1" />{s.hours && Object.keys(s.hours).length ? "Edit schedule" : "Set schedule"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-xl text-red-600" data-testid={`remove-staff-${s.id}`} onClick={() => removeStaff(s.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>

            {expanded[s.id] && drafts[s.id] && (
              <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`schedule-editor-${s.id}`}>
                <div className="space-y-2">
                  {DAYS.map((d) => {
                    const row = drafts[s.id][d] || { open: false, start: "09:00", end: "17:00" };
                    return (
                      <div key={d} className="flex items-center gap-3 text-sm">
                        <label className="flex items-center gap-2 w-24">
                          <input type="checkbox" checked={!!row.open} onChange={(e) => setDay(s.id, d, { open: e.target.checked })} data-testid={`sched-${s.id}-${d}-open`} />
                          <span className="font-medium">{DAY_SHORT[d]}</span>
                        </label>
                        {row.open ? (
                          <>
                            <Input type="time" value={row.start} onChange={(e) => setDay(s.id, d, { start: e.target.value })} className="w-28 h-8" data-testid={`sched-${s.id}-${d}-start`} />
                            <span className="text-slate-400">–</span>
                            <Input type="time" value={row.end} onChange={(e) => setDay(s.id, d, { end: e.target.value })} className="w-28 h-8" data-testid={`sched-${s.id}-${d}-end`} />
                          </>
                        ) : <span className="text-xs text-slate-400">off</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" className="rounded-xl bg-blue-600 hover:bg-blue-700" data-testid={`schedule-save-${s.id}`} onClick={() => saveSchedule(s.id)}>Save schedule</Button>
                  <Button size="sm" variant="ghost" className="rounded-xl" data-testid={`schedule-clear-${s.id}`} onClick={() => clearSchedule(s.id)}><X className="w-4 h-4 mr-1" />Clear (works all business hours)</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
